// Angular
import { Component } from '@angular/core';
import { Router } from '@angular/router';
// Model
import {
	BroadcastEventModel,
	BroadcastService,
	CONTENT_LOADER_EVENT,
	ToasterType
} from '@cv-invision/core';
import { ReasonType } from '../model/reason-types';
import { RecallPayloadModel, ResubmitManuallyCreatedOrderModel } from './model/resubmit-order.model';
import { EngagementFileChange } from '../../shared/engagement-file-upload/model/engagement-file-upload.model';
// Service
import { OrderInboxService } from '../service/order-inbox.service';
// Store
import { Store } from '@ngxs/store';
import { InboxState } from '../state/inbox.state';
import {
	SetOrderDocumentManually,
	SetOrderisRecalled
} from '../../quality/state/actions/quality.actions';
import { lastValueFrom } from 'rxjs';


export const RESUBMIT_ORDER_EVENT = 'resubmitOrderEvent';
export const RELOAD_INBOX = 'reloadInboxEvent';

export interface ResubmitCreatedOrder {
	orderId?: number;
	file?: File;
}

@Component({
	selector: 'cv-resubmit',
	templateUrl: './resubmit-order.component.html',
	styleUrls: ['./resubmit-order.component.scss']
})
export class ReSubmitOrderComponent {
	public resubmitManuallyCreatedOrder: ResubmitManuallyCreatedOrderModel = {
		orderId: '',
		file: null,
		engagementFile: null
	};
	public reasonTypes: ReasonType[] = [];
	public reasonForm = {
        reason: '',
        reasonType: ''
    }

	isResubmit: boolean;
	dataOrder: any;
	isImported: boolean;
	canResubmit: boolean;
	resubmitCreatedForm: ResubmitCreatedOrder = {
		orderId: null,
		file: null
	};
	public filesDropMessage = 'Drag and drop your XML file here, or click to';
	public uploadLabelText = 'Upload Mismo XML File';
	public fileDropped = false;
	public isEngagementFileValid = true;
	public mismoFileErrorMessage: string | null = null;
	public isManual = false;

	constructor(
		private inboxService: OrderInboxService,
		private broadcastService: BroadcastService,
		private router: Router,
		private store: Store
	) {
		this.canResubmit = false;
		this.isImported = true;
		this.isResubmit = false;
		this.registerResubmitListener();
		this.loadReasonTypes();
	}

	/**
	 * open rejection box
	 */
	toggleReSubmitBox(): void {
		this.isResubmit = false;
		this.dataOrder = null;
		this.resubmitCreatedForm.file = null;
		this.canResubmit = false;
		this.resubmitCreatedForm.orderId = null;
		this.resubmitManuallyCreatedOrder.orderId = '';
		this.resubmitManuallyCreatedOrder.file = null;
		this.resubmitManuallyCreatedOrder.engagementFile = null;
		this.fileDropped = false;
		this.mismoFileErrorMessage = null;
		this.isEngagementFileValid = true;
		this.isManual = false;
	}

	/**
	 * Get the latest file Uploaded
	 * @param event
	 */
	public onFilesDropped(files: File[]): void {
		if (files.length > 1) {
			files.shift();
		}

		const selectedFile = files[0];
		const schema = this.dataOrder?.schema ?? '';

		const isZip = selectedFile.name.endsWith('.zip');
		const isXML =
			selectedFile.type === 'application/xml' ||
			selectedFile.type === 'text/xml' ||
			selectedFile.name.endsWith('.xml');

		if (this.isManual) {
			this.resubmitManuallyCreatedOrder.file = selectedFile;
			this.fileDropped = !!files.length;

			const is36GSE = schema.includes('3.6GSE');

			if ((is36GSE && isZip) || (!is36GSE && isXML)) {
				this.mismoFileErrorMessage = null;
			} else {
				this.mismoFileErrorMessage = is36GSE
					? 'Only ZIP files can be uploaded. Please try again.'
					: 'Only XML files can be uploaded. Please try again.';
			}
		} else {
			this.canResubmit = !!files.length;
			this.resubmitCreatedForm.file = selectedFile;
		}
	}

	/**
	 * resubmit an order that has been imported
	 */
	reSubmitImported(): void {
		this.isResubmit = false;
		this.showLoader(true);
		const externalId = this.dataOrder.externalId;
		const externalProvider = this.dataOrder.externalProvider;
		const orderId = this.dataOrder.id;

		const recallPayload: RecallPayloadModel = {
			orderId: orderId,
			reasonType: this.reasonForm.reasonType,
			reason: this.reasonForm.reason
		}

		this.dataOrder = null;
		this.inboxService.reSubmitOrderImported(recallPayload).subscribe(
			response => {
				this.showLoader(false);
				if (response.isValid) {
					this.setResumitOrder(orderId);
				}
			},
			error => {
				this.showLoader(false);
				// Something goes wrong, update the List
				this.broadcastService.broadcast({
					name: RELOAD_INBOX,
					event: { reload: true }
				});
			}
		);
	}

	    /**
     * 
     * Loads the reason types for the reopen operation
     */
	public async loadReasonTypes() {
		try {
			this.reasonTypes = await lastValueFrom(this.inboxService.getReasonTypes()); 
		} catch (err) {
			console.error(err)
		}
	}
	
	/**
     * Handles the reason input text change
     * @param {event}
     */
	public onReasonChange(event: any): void{
		if(event) {
			this.reasonForm.reason = event?.value ?? '';
		}
	}
		
	/**
	 * Handles the reason category dropdown change
	 * @param {ReasonType} event 
	 */
	public onReasonCategoryChange(event: ReasonType): void {
		if(event){
			this.reasonForm.reasonType = event.key;
		}
	}
	
	/**
     * Returns the validity of the required form fields.
     * @returns {boolean}
     */
	isReasonFormInvalid() {
		return !(this.reasonForm.reason && this.reasonForm.reasonType);
	}

	/**
	 *re resubmit an order thats bee create
	 */
	reSubmitCreated(): void {
		if (this.isManual) {
			this.showLoader(true);
			this.handleResubmitManuallyCreatedOrder();
		} else {
			this.isResubmit = false;
			this.showLoader(true);
			this.dataOrder = null;
			this.inboxService.reSubmitOrderCreated(this.resubmitCreatedForm).subscribe(
				response => {
					this.showLoader(false);
					if (response.isValid) {
						this.setResumitOrder(this.resubmitCreatedForm.orderId);
					}
				},
				error => {
					this.showLoader(false);
					// Something goes wrong, update the List
					this.broadcastService.broadcast({
						name: RELOAD_INBOX,
						event: { reload: true }
					});
				}
			);
		}
	}

	/**
	 * Handles the resubmission of a manually created order.
	 *
	 * Validates that required fields are present (orderId and XML file),
	 * prepares the FormData payload, and sends the request to the API.
	 * 
	 * If successful, updates the UI and internal state. If an error occurs,
	 * shows an error toast and triggers a reload of the inbox list.
	 */
	private handleResubmitManuallyCreatedOrder() {
		const { orderId, file, engagementFile } = this.resubmitManuallyCreatedOrder;

		if (!orderId || !(file instanceof File)) {
			this.broadcastService.broadcast({
				name: ToasterType.ERROR,
				event: { message: 'Please complete all required fields before submitting.' }
			});
			return;
		}

		const formData = new FormData();
		formData.append('file', file);

		if (engagementFile instanceof File) {
			formData.append('engagementLetterFile', engagementFile);
		}

		this.inboxService.resubmitManuallyCreatedOrder(formData, orderId).subscribe({
			next: (res) => {
				this.showLoader(false);
				if (res.isValid && orderId !== undefined) {
					this.setResumitOrder(this.resubmitManuallyCreatedOrder.orderId);
				}
			},
			error: (err) => {
				console.error('Order creation failed:', err);
				this.broadcastService.broadcast({
					name: ToasterType.ERROR,
					event: { message: 'Something went wrong. Please try again.' }
				});

				this.showLoader(false);
				// Something goes wrong, update the List
				this.broadcastService.broadcast({
					name: RELOAD_INBOX,
					event: { reload: true }
				});
			}
		});
	}

	/**
	 * set order doc and redirect order
	 */
	private setResumitOrder(orderId: number | string): void {
		const selectedOrderId = this.store.selectSnapshot(InboxState.getSelectedOrderId);
		if (orderId === selectedOrderId) {
			this.store.dispatch(new SetOrderDocumentManually(null));
		}
		this.broadcastService.broadcast({
			name: ToasterType.SUCCESS,
			event: {
				message: `Order ${selectedOrderId} has started the re-import process. Please wait until it completes — this may take around one minute.`
			}
		});
		setTimeout(() => {
			this.toggleReSubmitBox();
			this.store.dispatch(new SetOrderisRecalled(true));
		}, 500);
	}

	/**
	 * Register Lister to Show/Hide the Loader
	 */
	private registerResubmitListener(): void {
		this.broadcastService.on(RESUBMIT_ORDER_EVENT, (broadcast: BroadcastEventModel) => {
			this.isResubmit = broadcast.event.isResubmit;
			this.dataOrder = broadcast.event.data;
			const { externalId, externalProvider, id, externalTrackingProvider, schema } = this.dataOrder;

			// Set file message and label based on schema
			if (schema?.includes('3.6GSE')) {
				this.filesDropMessage = 'Upload the ZIP file that you want to recall.';
				this.uploadLabelText = 'Upload Mismo ZIP File';
			} else {
				this.filesDropMessage = 'Upload the XML file that you want to recall.';
				this.uploadLabelText = 'Upload Mismo XML File';
			}

			this.isManual =
				externalProvider === 'Manual' &&
				externalTrackingProvider === 'Manual';

			if (this.isManual) {
				this.isImported = false;
				this.resubmitManuallyCreatedOrder.orderId = id
			} else if (!externalId && !externalProvider) {
				this.isImported = false;
				this.resubmitCreatedForm.orderId = id;
			}
			else {
				this.isImported = true;
			}
		});
	}

	/**
	 * Show/Hide the Loader
	 * @param isShowing
	 */
	private showLoader(isShowing: boolean): void {
		this.broadcastService.broadcast({
			name: CONTENT_LOADER_EVENT,
			event: {
				isLoader: isShowing,
				message: 'We are recalling the Order'
			}
		});
	}

	/**
	 * Handles file change events emitted from the engagement file upload component.
	 * Updates the local engagementFile and its validation status.
	 *
	 * @param event - The file change event containing the selected file and its validation result.
	 */
	public onEngagementFileSelect(event: EngagementFileChange): void {
		this.resubmitManuallyCreatedOrder.engagementFile = event.file;
		this.isEngagementFileValid = event.isValid;
	}

	/**
	 * Clears the currently selected engagement file and marks it as valid.
	 * This is typically triggered when the user removes the file manually.
	 */
	public removeEngagementFile(): void {
		this.resubmitManuallyCreatedOrder.engagementFile = null;
		this.isEngagementFileValid = true;
	}
}